import { usePrimaryColor } from '../../branding/usePrimaryColor';
import { formatCurrency } from '../../utils/currency';
import type { PipelineDay } from '../../types/dashboard';

interface PipelineRevenueProps {
  totalProjected: number;
  bookingCount: number;
  byDay: PipelineDay[];
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  periodLabel?: string;
  onRetry?: () => void;
}

/** Max upcoming days to show in the breakdown list. */
const MAX_DAYS = 7;

export function PipelineRevenue({
  totalProjected,
  bookingCount,
  byDay,
  loading = false,
  error = false,
  forbidden = false,
  periodLabel,
  onRetry,
}: PipelineRevenueProps) {
  const primaryColor = usePrimaryColor();

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Revenue pipeline</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Revenue pipeline</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load pipeline data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && bookingCount === 0 && byDay.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-32 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="h-8 w-28 bg-canvas animate-pulse rounded" />
          <div className="h-3 w-24 bg-canvas animate-pulse rounded" />
          <div className="space-y-2 pt-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="h-3 w-20 bg-canvas animate-pulse rounded" />
                <div className="h-3 w-14 bg-canvas animate-pulse rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (bookingCount === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold">Revenue pipeline</h3>
            {periodLabel && (
              <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
            )}
          </div>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            No confirmed sessions upcoming in this period.
          </p>
        </div>
      </div>
    );
  }

  const visible = byDay.slice(0, MAX_DAYS);
  const remaining = byDay.length - MAX_DAYS;
  const maxAmount = Math.max(...byDay.map((d) => d.amount), 1);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Revenue pipeline</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
      </div>

      {/* Main figure + daily breakdown */}
      <div className="px-4 pt-4 pb-3">
        {/* Total projected */}
        <div className="mb-1">
          <span className="text-[28px] font-semibold leading-none num">
            ${formatCurrency(totalProjected)}
          </span>
        </div>
        <p className="text-[11.5px] text-ink-4 mb-4">
          {bookingCount} confirmed session{bookingCount !== 1 ? 's' : ''} upcoming
        </p>

        {/* By-day breakdown bars */}
        <div className="space-y-2">
          {visible.map((day, i) => {
            const widthPct = Math.max((day.amount / maxAmount) * 100, day.amount > 0 ? 3 : 0);
            const opacity = Math.max(1 - i * 0.12, 0.35);
            const dateLabel = formatDayLabel(day.date);

            return (
              <div key={day.date} className="flex items-center gap-3">
                <span className="text-[11.5px] text-ink-3 w-20 truncate shrink-0" title={day.date}>
                  {dateLabel}
                </span>
                <div className="flex-1 h-5 rounded overflow-hidden bg-canvas relative">
                  <div
                    className="h-full rounded transition-all duration-300"
                    style={{
                      width: `${widthPct}%`,
                      backgroundColor: primaryColor,
                      opacity,
                    }}
                  />
                </div>
                <span className="text-[12px] font-medium num shrink-0 w-16 text-right">
                  ${formatCurrency(day.amount)}
                </span>
                <span className="text-[10.5px] text-ink-4 num shrink-0 w-6 text-right">
                  {day.count}
                </span>
              </div>
            );
          })}

          {remaining > 0 && (
            <div className="text-[10.5px] text-ink-4 pt-1">
              and {remaining} more day{remaining !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Format a YYYY-MM-DD date into a short label like "Mon Jun 9". */
function formatDayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()}`;
}
